import React, { useState } from "react";
import { apiFetch } from "../../services/api";
import LoadingButton from "../../components/common/LoadingButton";
import V2AuthLayout from "../components/V2AuthLayout";
import AuthConnectingStatus, {
  AUTH_CONNECTING_INITIAL,
} from "../components/AuthConnectingStatus";
import { useV2Theme } from "../theme/ThemeProvider";
import GoogleContinueButton from "../components/GoogleContinueButton";
import CompanySsoButton from "../components/CompanySsoButton";
import AuthMagicLinkForm from "../components/auth/AuthMagicLinkForm";
import AuthModeToggle from "../components/auth/AuthModeToggle";
import {
  AUTH_INPUT_CLASS,
  AUTH_LINK_CLASS,
} from "../components/auth/authFieldStyles";
import {
  continueWithGoogle,
  persistAuthSession,
} from "../auth/googleSso";
import { startCompanyOidcSso } from "../auth/oidcSso";

const DEMO_ENTRY_PATH = "/demo";

export default function V2LoginPage({
  onLoginSuccess,
  onSignup,
  onForgotPassword,
  onNeedsGoogleSignup,
  sessionNotice = "",
}) {
  const { tokens } = useV2Theme();
  const [mode, setMode] = useState("password"); // password | magic
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [terms, setTerms] = useState(false);
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [oidcLoading, setOidcLoading] = useState(false);
  const [companySsoPrompt, setCompanySsoPrompt] = useState(false);
  const [companySsoEmail, setCompanySsoEmail] = useState("");
  const [error, setError] = useState("");
  const [showResendVerification, setShowResendVerification] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState("");

  const clearFeedback = () => {
    setError("");
    setShowResendVerification(false);
    setResendMessage("");
  };

  const goToCreateAccount = () => {
    if (typeof onSignup === "function") {
      onSignup();
      return;
    }
    window.location.assign(DEMO_ENTRY_PATH);
  };

  const domainFromWorkEmail = (email) => {
    const normalized = String(email || "").trim().toLowerCase();
    const at = normalized.lastIndexOf("@");
    if (at < 1 || at === normalized.length - 1) return null;
    const domain = normalized.slice(at + 1).trim();
    if (!domain || !domain.includes(".") || domain.includes(" ")) return null;
    return domain;
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (error) clearFeedback();
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    clearFeedback();

    if (formData.password.length > 72) {
      setError("Password is too long. Please use a password with 72 characters or less.");
      setLoading(false);
      return;
    }

    try {
      const response = await apiFetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem("access_token", data.access_token);
        localStorage.setItem("user", JSON.stringify(data.user));
        onLoginSuccess?.(data.user);
      } else {
        let message = "Login failed";
        let isEmailNotVerified = false;
        try {
          const errorData = await response.json();
          if (errorData?.detail) {
            if (typeof errorData.detail === "string") {
              message = errorData.detail;
            } else if (typeof errorData.detail === "object" && errorData.detail.message) {
              message = errorData.detail.message;
              if (errorData.detail.code === "AUTH_EMAIL_NOT_VERIFIED") {
                isEmailNotVerified = true;
              }
              if (errorData.detail.code === "AUTH_SSO_ONLY") {
                message =
                  errorData.detail.message ||
                  "This account uses Google sign-in. Please use Continue with Google.";
              }
            } else {
              message = String(errorData.detail);
            }
          }
        } catch {
          /* ignore */
        }
        if (response.status === 401 && !isEmailNotVerified) {
          message = "Invalid user id or password";
        }
        if (response.status === 403) {
          const looksUnverified =
            message.toLowerCase().includes("verify your email") ||
            message.toLowerCase().includes("email not verified");
          isEmailNotVerified = looksUnverified;
        }
        setError(message);
        setShowResendVerification(isEmailNotVerified);
      }
    } catch (err) {
      const msg =
        err && typeof err.message === "string" && err.message.includes("Invalid")
          ? "Invalid user id or password"
          : "Network error. Please try again.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleMagicLink = async (e) => {
    e.preventDefault();
    if (!formData.email.trim()) return;
    setLoading(true);
    clearFeedback();
    try {
      const res = await apiFetch("/api/auth/magic-link/request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: formData.email.trim(),
          terms_accepted: terms,
          gdpr_consent: terms,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(
          (typeof data.detail === "string" && data.detail) ||
            "Could not send the sign-in link. Please try again."
        );
        return;
      }
      setSent(true);
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    setGoogleLoading(true);
    clearFeedback();
    try {
      const result = await continueWithGoogle();
      if (result.ok) {
        persistAuthSession(result.data);
        onLoginSuccess?.(result.data.user);
        return;
      }
      if (result.needsSignupDetails) {
        onNeedsGoogleSignup?.({
          idToken: result.idToken,
          email: result.email,
          displayName: result.displayName,
        });
        return;
      }
      setError(result.message || "Google sign-in failed");
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!formData.email) {
      setResendMessage("Please enter your email address first");
      return;
    }
    if (resendLoading) return;

    setResendLoading(true);
    setResendMessage("");
    try {
      const response = await apiFetch("/api/auth/resend-verification", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: formData.email }),
      });

      const data = await response.json().catch(() => ({}));

      if (response.ok && data.sent === true) {
        setResendMessage("Verification email sent! Please check your inbox.");
        setTimeout(() => setResendMessage(""), 5000);
      } else if (response.ok && data.sent === false) {
        setResendMessage(
          "We could not send the email right now. Please try again in a moment."
        );
      } else {
        setResendMessage(
          data.detail?.message ||
            data.detail ||
            "Failed to send verification email. Please try again."
        );
      }
    } catch {
      setResendMessage("Network error. Please try again.");
    } finally {
      setResendLoading(false);
    }
  };

  const handleCompanySso = () => {
    clearFeedback();
    setCompanySsoEmail((prev) => prev || formData.email || "");
    setCompanySsoPrompt(true);
  };

  const handleCompanySsoContinue = (e) => {
    e?.preventDefault?.();
    clearFeedback();
    const domain = domainFromWorkEmail(companySsoEmail);
    if (!domain) {
      setError("Enter your work email (for example you@company.com).");
      return;
    }
    setOidcLoading(true);
    startCompanyOidcSso({ domain });
  };

  const busy = loading || googleLoading || oidcLoading;

  return (
    <V2AuthLayout
      title="Sign in"
      subtitle="Architecture Decision Workspace"
    >
      <div className="space-y-3.5">
        {sessionNotice ? (
          <div
            className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900"
            role="status"
          >
            {sessionNotice}
          </div>
        ) : null}

        {sent ? (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
            Check your email — open the sign-in link{" "}
            <strong>on this device</strong> to continue.
          </div>
        ) : (
          <>
            <AuthModeToggle
              value={mode}
              onChange={(next) => {
                setMode(next);
                setSent(false);
                clearFeedback();
              }}
              left={{ id: "password", label: "Email & password" }}
              right={{ id: "magic", label: "Email link" }}
            />

            {mode === "password" ? (
              <form className="space-y-3.5" onSubmit={handlePasswordSubmit}>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-slate-700">
                    Email
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    className={AUTH_INPUT_CLASS}
                    placeholder="you@organization.com"
                    value={formData.email}
                    onChange={handleChange}
                  />
                </div>
                <div>
                  <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
                    <label htmlFor="password" className="block text-sm font-medium text-slate-700">
                      Password
                    </label>
                    <button
                      type="button"
                      onClick={onForgotPassword}
                      className={`${AUTH_LINK_CLASS} self-start sm:self-auto`}
                    >
                      Forgot your password?
                    </button>
                  </div>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="current-password"
                    required
                    maxLength={72}
                    className={AUTH_INPUT_CLASS}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={handleChange}
                  />
                </div>

                {error ? (
                  <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
                    {showResendVerification ? (
                      <>
                        <p className="font-medium">Email not verified</p>
                        <p className="mt-2 text-rose-700/90">
                          Check your inbox for the verification link from signup, or resend the email below.
                        </p>
                        <div className="mt-3">
                          <LoadingButton
                            type="button"
                            onClick={handleResendVerification}
                            loading={resendLoading}
                            loadingLabel="Sending…"
                            invert
                            className={`w-full ${tokens.button.primary} disabled:opacity-50`}
                          >
                            Resend verification email
                          </LoadingButton>
                          {resendMessage ? (
                            <p
                              className={`mt-2 text-xs text-center ${
                                resendMessage.includes("sent") ? "text-emerald-700" : "text-rose-700"
                              }`}
                            >
                              {resendMessage}
                            </p>
                          ) : null}
                        </div>
                      </>
                    ) : (
                      error
                    )}
                  </div>
                ) : null}

                <LoadingButton
                  type="submit"
                  loading={loading}
                  loadingLabel={AUTH_CONNECTING_INITIAL}
                  invert
                  disabled={busy}
                  className={`w-full ${tokens.button.primary} disabled:opacity-50`}
                >
                  Sign in
                </LoadingButton>
              </form>
            ) : (
              <AuthMagicLinkForm
                email={formData.email}
                onEmailChange={handleChange}
                terms={terms}
                onTermsChange={setTerms}
                onSubmit={handleMagicLink}
                loading={loading}
                disabled={busy}
                termsVariant="signin"
                error={error}
                buttonClassName={`w-full ${tokens.button.primary} disabled:opacity-50`}
              />
            )}

            <GoogleContinueButton
              onClick={handleGoogle}
              loading={googleLoading}
              disabled={busy}
              loadingLabel={AUTH_CONNECTING_INITIAL}
            />

            <div className="space-y-1.5">
              {!companySsoPrompt ? (
                <CompanySsoButton
                  onClick={handleCompanySso}
                  loading={oidcLoading}
                  disabled={busy}
                />
              ) : (
                <form onSubmit={handleCompanySsoContinue} className="space-y-3">
                  <label className="block text-sm font-medium text-slate-700">
                    Work email
                    <input
                      type="email"
                      name="company_sso_email"
                      autoComplete="username"
                      required
                      value={companySsoEmail}
                      onChange={(ev) => {
                        setCompanySsoEmail(ev.target.value);
                        if (error) clearFeedback();
                      }}
                      placeholder="you@company.com"
                      className={AUTH_INPUT_CLASS}
                      disabled={busy}
                    />
                  </label>
                  <LoadingButton
                    type="submit"
                    loading={oidcLoading}
                    loadingLabel="Redirecting…"
                    disabled={busy}
                    className="flex w-full items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-800 shadow-sm hover:bg-slate-50 disabled:opacity-50"
                  >
                    Continue to company SSO
                  </LoadingButton>
                  <button
                    type="button"
                    className={`w-full text-center ${AUTH_LINK_CLASS}`}
                    disabled={busy}
                    onClick={() => {
                      setCompanySsoPrompt(false);
                      clearFeedback();
                    }}
                  >
                    Cancel
                  </button>
                </form>
              )}
              <p className="text-center text-xs text-slate-500">
                Only for organisations that have already configured company SSO
                (Admin → People & access → Company SSO). We use your email domain
                to open the right identity provider. Individuals and new accounts
                should use Google or email.
              </p>
            </div>

            <AuthConnectingStatus active={busy} />
          </>
        )}

        {sent ? (
          <button
            type="button"
            onClick={() => {
              setSent(false);
              clearFeedback();
            }}
            className={`w-full text-center ${AUTH_LINK_CLASS}`}
          >
            Use a different email
          </button>
        ) : (
          <p className="text-center text-sm text-slate-600">
            Don&apos;t have an account?{" "}
            <button type="button" onClick={goToCreateAccount} className={AUTH_LINK_CLASS}>
              See it live — create account
            </button>
          </p>
        )}
      </div>
    </V2AuthLayout>
  );
}
